// shell/components/entity-fields/form-renderers/select-input.ts

import { createElement } from '../../../utils/dom';
import type { SelectFormField, SelectOption } from '../../entity-view/types';
import type { FormRendererResult } from './types';

/**
 * Renders a select dropdown, optionally with prev/next nudge buttons.
 */
export function renderSelectInput<T>(
    field: SelectFormField<T>,
    initialValue: unknown,
    onChange: (value: unknown) => void
): FormRendererResult {
    // Track current options so getValue/setOptions can reference them
    let currentOptions = [...field.options];

    const container = createElement('div', {
        className: 'ev-field-input ev-field-input--select',
    });

    const selectEl = createElement('select', {
        className: 'ev-select',
        attributes: {
            ...(field.disabled ? { disabled: '' } : {}),
            ...(field.required ? { 'aria-required': 'true' } : {}),
        },
    }) as HTMLSelectElement;

    // Populate options
    function populateOptions(options: SelectOption[], preserveValue?: string): void {
        // Remove all non-placeholder options
        while (selectEl.options.length > (field.placeholder ? 1 : 0)) {
            selectEl.remove(selectEl.options.length - 1);
        }
        for (const option of options) {
            const optionEl = createElement('option', {
                textContent: option.label,
                attributes: { value: String(option.key) },
            }) as HTMLOptionElement;
            selectEl.appendChild(optionEl);
        }
        if (preserveValue != null) {
            selectEl.value = String(preserveValue);
            // If the value no longer exists, reset to first option
            if (selectEl.selectedIndex === -1 && selectEl.options.length > 0) {
                selectEl.selectedIndex = 0;
            }
        }
    }

    // Add placeholder option if provided
    if (field.placeholder) {
        const placeholderOption = createElement('option', {
            textContent: field.placeholder,
            attributes: { value: '', disabled: '', selected: '' },
        }) as HTMLOptionElement;
        selectEl.appendChild(placeholderOption);
    }

    // Initial options
    populateOptions(currentOptions);

    // Set initial value
    if (initialValue != null) {
        selectEl.value = String(initialValue);
    }

    // Handle change
    const handleChange = () => {
        const selectedOption = currentOptions.find(o => String(o.key) === selectEl.value);
        onChange(selectedOption ? selectedOption.key : null);
    };
    selectEl.addEventListener('change', handleChange);

    // Nudge buttons (prev/next)
    let prevBtn: HTMLButtonElement | null = null;
    let nextBtn: HTMLButtonElement | null = null;

    if (field.nudge) {
        const nudgeContainer = createElement('div', {
            className: 'ev-select-nudge',
        });

        prevBtn = createElement('button', {
            className: 'ev-nudge-btn',
            textContent: '\u25C0', // ◀
            attributes: { type: 'button' },
        }) as HTMLButtonElement;

        nextBtn = createElement('button', {
            className: 'ev-nudge-btn',
            textContent: '\u25B6', // ▶
            attributes: { type: 'button' },
        }) as HTMLButtonElement;

        prevBtn.addEventListener('click', () => {
            const idx = selectEl.selectedIndex;
            const minIdx = field.placeholder ? 1 : 0;
            if (idx > minIdx) {
                selectEl.selectedIndex = idx - 1;
                handleChange();
            }
        });

        nextBtn.addEventListener('click', () => {
            const idx = selectEl.selectedIndex;
            if (idx < selectEl.options.length - 1) {
                selectEl.selectedIndex = idx + 1;
                handleChange();
            }
        });

        nudgeContainer.appendChild(prevBtn);
        nudgeContainer.appendChild(selectEl);
        nudgeContainer.appendChild(nextBtn);
        container.appendChild(nudgeContainer);
    } else {
        container.appendChild(selectEl);
    }

    return {
        element: container,
        getValue: () => {
            const selectedOption = currentOptions.find(o => String(o.key) === selectEl.value);
            return selectedOption ? selectedOption.key : null;
        },
        setValue: (value: unknown) => {
            selectEl.value = value != null ? String(value) : '';
        },
        validate: async () => true,
        focus: () => selectEl.focus(),
        dispose: () => {
            selectEl.removeEventListener('change', handleChange);
        },
        setDisabled: (disabled: boolean) => {
            selectEl.disabled = disabled;
            if (prevBtn) prevBtn.disabled = disabled;
            if (nextBtn) nextBtn.disabled = disabled;
        },
        setOptions: (options: { key: unknown; label: string }[]) => {
            const currentValue = selectEl.value;
            currentOptions = options as SelectOption[];
            populateOptions(currentOptions, currentValue);
        },
    };
}
